import React, { useEffect, useMemo, useRef, useState } from "react";
import FontAwesome from "react-fontawesome";
import { useTranslation } from "react-i18next";
import SearchBox from "../../../../common/components/search/searchBox";
import { filterMarketsBySearch, getMarketSearchState } from "./navigationSearch";

// Market-specific navigation subtree that groups market links by product and manages local expansion/search.
export default function MarketSelector({
  markets,
  expandedGroups,
  setExpandedGroups,
  listKey,
  component,
  routePrefix,
  namePrefix,
  parentIds,
  renderMenuItem,
  manageExternalMarkets,
  onItemClick,
  activeMenuItems,
  enableSearch = false,
  forceExpandAll = false,
  autoExpandedProductIds = [],
  navigationSearchValue = "",
  termSchedules,
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [originalExpanded, setOriginalExpanded] = useState(null);
  const previousNavigationSearchRef = useRef("");
  const autoExpandedProductIdsRef = useRef(new Set());
  const selectorNestingLevel = Math.max(parentIds?.length || 0, 0);
  const normalizedRoutePrefix = useMemo(() => {
    if (!routePrefix) return "/";

    const withLeadingSlash = routePrefix.startsWith("/")
      ? routePrefix
      : `/${routePrefix}`;

    return withLeadingSlash.endsWith("/")
      ? withLeadingSlash
      : `${withLeadingSlash}/`;
  }, [routePrefix]);

  const filteredMarkets = useMemo(() => {
    if (!enableSearch) {
      return markets;
    }
    
    return filterMarketsBySearch(markets, search);
  }, [markets, search, enableSearch]);

  const groupedProducts = useMemo(() => {
    const map = {};

    for (const market of filteredMarkets) {
      if (!map[market.productId]) {
        map[market.productId] = {
          productName: market.productName,
          markets: [],
        };
      }
      map[market.productId].markets.push(market);
    }

    Object.values(map).forEach((entry) => {
      entry.markets.sort((a, b) => a.name.localeCompare(b.name));
    });

    return Object.entries(map).sort(([, a], [, b]) =>
      a.productName.localeCompare(b.productName)
    );
  }, [filteredMarkets]);

  useEffect(() => {
    const normalizedNavigationSearch = navigationSearchValue.trim().toLowerCase();

    if (!normalizedNavigationSearch) {
      previousNavigationSearchRef.current = "";
      autoExpandedProductIdsRef.current = new Set();
      return;
    }

    const previousAutoExpandedProductIds = new Set(
      autoExpandedProductIdsRef.current
    );
    const nextAutoExpandedProductIds = new Set(autoExpandedProductIds);

    if (
      previousNavigationSearchRef.current === normalizedNavigationSearch &&
      JSON.stringify(Array.from(previousAutoExpandedProductIds).sort()) ===
        JSON.stringify(Array.from(nextAutoExpandedProductIds).sort())
    ) {
      return;
    }

    previousNavigationSearchRef.current = normalizedNavigationSearch;
    autoExpandedProductIdsRef.current = nextAutoExpandedProductIds;

    setExpandedGroups((prev) => {
      const expandedSet = new Set(prev[listKey] || []);
      let didChange = false;

      previousAutoExpandedProductIds.forEach((productId) => {
        if (!nextAutoExpandedProductIds.has(productId) && expandedSet.has(productId)) {
          expandedSet.delete(productId);
          didChange = true;
        }
      });

      nextAutoExpandedProductIds.forEach((productId) => {
        if (!expandedSet.has(productId)) {
          expandedSet.add(productId);
          didChange = true;
        }
      });

      return didChange
        ? {
            ...prev,
            [listKey]: Array.from(expandedSet),
          }
        : prev;
    });
  }, [
    autoExpandedProductIds,
    listKey,
    navigationSearchValue,
    setExpandedGroups,
  ]);

  const toggleProduct = (productId) => {
    setExpandedGroups((prev) => {
      const expandedSet = new Set(prev[listKey] || []);

      if (expandedSet.has(productId)) {
        expandedSet.delete(productId);
      } else {
        expandedSet.add(productId);
      }

      return {
        ...prev,
        [listKey]: Array.from(expandedSet),
      };
    });
  };

  const handleSearchChange = (value) => {
    if (value && originalExpanded === null) {
      setOriginalExpanded(expandedGroups[listKey]);
    }

    if (!value) {
      if (originalExpanded !== null) {
        setExpandedGroups((prev) => ({
          ...prev,
          [listKey]: originalExpanded,
        }));
      }
      setOriginalExpanded(null);
    }

    setSearch(value);

    if (value) {
      const { autoExpandedProductIds: matchingMarketProductIds } =
        getMarketSearchState(markets, value);

      setExpandedGroups((prev) => ({
        ...prev,
        [listKey]: matchingMarketProductIds,
      }));
    }
  };

  const searchLabel = t("SearchMarkets");

  return (
    <div
      className={`generic-market-selector market-selector-level-${selectorNestingLevel}`}
    >
      {enableSearch && (
        <div className="menu-search-wrapper" role="search">
          <SearchBox
            placeholder={searchLabel}
            value={search}
            onChange={handleSearchChange}
            aria-label={searchLabel}
          />
        </div>
      )}

      {markets.length === 0 && (
        <div className="menu-product-empty-state">
          {t("NoMarketSubscriptions")}
        </div>
      )}

      {groupedProducts.map(([productId, { productName, markets: productMarkets }]) => {
        const expanded =
          forceExpandAll || (expandedGroups[listKey] || []).includes(productId);
        const hasActiveChild = productMarkets.some((market) =>
          activeMenuItems?.has(`${normalizedRoutePrefix}${market.marketId}`)
        );

        return (
          <div
            key={productId}
            className={`menu-product-node ${expanded ? "expanded" : ""} ${
              hasActiveChild ? "has-active-child" : ""
            }`.trim()}
          >
            <button
              type="button"
              className={`menu-product-header ${expanded ? "expanded" : ""} ${
                hasActiveChild ? "has-active-child" : ""
              }`.trim()}
              onClick={() => toggleProduct(productId)}
              aria-expanded={expanded}
            >
              <span className="menu-product-header-arrow">
                <FontAwesome
                  name="chevron-right"
                  className={expanded ? "expanded" : ""}
                />
              </span>
              <span className="menu-product-header-label">{productName}</span>
            </button>

            <div
              className="menu-product-children"
              style={{ display: expanded ? "block" : "none" }}
              aria-hidden={!expanded}
            >
              <div className="menu-product-markets" role="group" aria-label={productName}>
                {productMarkets.map((market) => {
                  const marketLabel = market.name;
                  const marketRoute = `${normalizedRoutePrefix}${market.marketId}`;

                  return renderMenuItem({
                    to: marketRoute,
                    icon: "",
                    text: marketLabel,
                    name: namePrefix + " - " + marketLabel,
                    onClick: onItemClick,
                    size: "small",
                    component: component,
                    id: listKey + "-" + market.marketId,
                    config: {
                      market,
                      termSchedules,
                      productId: market.productId,
                      route: marketRoute,
                      externalData: market.isExternal && manageExternalMarkets,
                      searchSessionRestore: {
                        listKey,
                        expandedGroupId: market.productId,
                      },
                    },
                    parentIds,
                    isSubmenuItem: true,
                  });
                })}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
